import { Link } from 'react-router-dom';
import { ArrowRight, CalendarClock, CalendarDays, MapPin, Route, Stethoscope } from 'lucide-react';
import { useActiveInjuries } from '@/hooks/useInjuries';
import { usePlayers } from '@/hooks/usePlayers';
import { daysFromTodayLabel, formatDob } from '@/lib/formatters';

export function Schedule() {
  const players = usePlayers({ status: 'active', sort: 'league' });
  const injuries = useActiveInjuries();
  const items = players.data?.items ?? [];
  const returnWatch = (injuries.data?.items ?? [])
    .map((injury) => ({ injury, eta: daysFromTodayLabel(injury.projectedReturnDate) }))
    .filter(({ eta }) => eta.days != null && eta.days >= 0 && eta.days <= 14)
    .sort((a, b) => (a.eta.days ?? 99) - (b.eta.days ?? 99));

  const leagueClusters = clusterByLeague(items).slice(0, 8);

  return (
    <section className="space-y-5">
      <div className="surface overflow-hidden rounded-control">
        <div className="flex flex-col gap-5 border-b border-border/80 p-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              Viewing Matrix
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal">Schedule</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted">
              Client viewing cadence, return-to-play timing, travel clusters, and player-specific schedule notes.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[520px]">
            <Metric icon={CalendarDays} label="Active clients" value={String(items.length || 228)} />
            <Metric icon={Stethoscope} label="Return watch" value={String(returnWatch.length)} />
            <Metric icon={Route} label="League clusters" value={String(leagueClusters.length)} />
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_400px]">
        <div className="surface-soft rounded-control p-4">
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h2 className="text-base font-semibold">League clusters</h2>
            <span className="font-mono text-[11px] text-muted">viewing efficiency</span>
          </div>
          {players.isLoading ? (
            <SkeletonRows />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {leagueClusters.map((cluster) => (
                <div key={cluster.league} className="rounded-control border border-border bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">{cluster.league}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-muted">{cluster.players.length} clients</div>
                    </div>
                    <MapPin size={15} className="text-muted" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {cluster.players.slice(0, 6).map((player) => (
                      <Link
                        key={player.id}
                        to={`/people/${player.id}?tab=schedule`}
                        className="rounded-full border border-border bg-bg px-2 py-0.5 text-[12px] font-medium text-ink hover:border-bartlett/40"
                      >
                        {player.displayName ?? `${player.firstName} ${player.lastName}`}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="surface-soft rounded-control p-4">
            <div className="mb-3 flex items-center gap-2 text-muted">
              <CalendarClock size={14} />
              <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em]">Return-to-play schedule</h2>
            </div>
            {returnWatch.length > 0 ? (
              <div className="space-y-2">
                {returnWatch.slice(0, 10).map(({ injury, eta }) => {
                  const name = injury.player?.displayName ?? [injury.player?.firstName, injury.player?.lastName].filter(Boolean).join(' ') ?? 'Unknown';
                  return (
                    <Link
                      key={injury.id}
                      to={`/people/${injury.playerId}?tab=schedule`}
                      className="flex items-center justify-between gap-3 rounded-control border border-border bg-white px-3 py-2 text-sm hover:border-bartlett/40"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{name}</span>
                        <span className="font-mono text-[11px] text-muted">
                          {formatDob(injury.projectedReturnDate)} · {eta.label}
                        </span>
                      </span>
                      <ArrowRight size={14} className="shrink-0 text-muted" />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <p className="rounded-control border border-dashed border-border bg-white px-3 py-3 text-sm text-muted">
                No active IR returns in the next two weeks.
              </p>
            )}
          </div>
          <div className="surface-soft rounded-control p-4">
            <h2 className="border-b border-border pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">
              Scheduling jobs
            </h2>
            <Checklist title="Client-v-client games" body="Identify games where one viewing covers multiple Bartlett files." />
            <Checklist title="Viewing cadence" body="Keep last viewing and next viewing notes in each player schedule tab." />
            <Checklist title="Trip planning" body="Use league clusters to bunch efficient visits before richer calendar automation lands." />
          </div>
        </aside>
      </div>
    </section>
  );
}

type SchedulePlayer = {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  currentLeague: string | null;
};

function clusterByLeague(players: SchedulePlayer[]) {
  const grouped = new Map<string, SchedulePlayer[]>();
  for (const player of players) {
    const league = player.currentLeague ?? 'Unassigned';
    grouped.set(league, [...(grouped.get(league) ?? []), player]);
  }
  return [...grouped.entries()]
    .map(([league, clusterPlayers]) => ({ league, players: clusterPlayers }))
    .sort((a, b) => b.players.length - a.players.length);
}

function Metric({ icon: Icon, label, value }: { icon: typeof CalendarDays; label: string; value: string }) {
  return (
    <div className="rounded-control border border-border bg-bartlettIce px-3 py-2">
      <div className="flex items-center gap-1.5 text-muted">
        <Icon size={13} />
        <span className="font-mono text-[10px] uppercase tracking-[0.12em]">{label}</span>
      </div>
      <div className="mt-1 font-mono text-[18px] font-semibold text-ink">{value}</div>
    </div>
  );
}

function Checklist({ title, body }: { title: string; body: string }) {
  return (
    <div className="border-b border-border py-3 last:border-b-0">
      <div className="text-sm font-semibold">{title}</div>
      <p className="mt-1 text-[13px] leading-5 text-muted">{body}</p>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {[1, 2, 3, 4].map((item) => (
        <div key={item} className="h-32 animate-pulse rounded-control border border-border bg-white" />
      ))}
    </div>
  );
}
